// ===== ./src/utils/backgroundUtils.ts =====
import { log } from "./debug";
import { resolveImageSrc } from "./resolveAssets";
import { isColor, normalizeColor } from "./color";

export type BgApplied = "none" | "color" | "image-h" | "image-v";

function isOff(v: unknown): boolean {
  if (v == null) return true;
  const s = String(v).trim().toLowerCase();
  return s === "" || s === "0" || s === "false" || s === "none" || s === "off";
}

function setBgImageTarget(
  target: HTMLElement,
  url: string,
  size: "cover" | "contain",
  pos: string
) {
  const prev = target.style.backgroundImage;
  const next = `url("${url}")`;
  if (prev === next &&
      target.style.backgroundSize === size &&
      target.style.backgroundPosition === pos) {
    return; // no-op if identical
  }
  target.style.backgroundImage = next;
  target.style.backgroundRepeat = "no-repeat";
  target.style.backgroundSize = size;
  target.style.backgroundPosition = pos;
}

// Normalize background-position tokens into a safe "x y" string
function normalizePos(raw: unknown, fallback: "center center" | "center top" | "center bottom" = "center center"): string {
  const v = String(raw ?? "").trim().toLowerCase();
  if (!v) return fallback;
  if (v === "top")    return "center top";
  if (v === "bottom") return "center bottom";
  if (v === "left")   return "left center";
  if (v === "right")  return "right center";
  if (v === "center") return "center center";
  const parts = v.split(/\s+/).slice(0, 2);
  if (parts.length === 2) return `${parts[0]} ${parts[1]}`;
  return fallback;
}

/**
 * Apply section background from attrs.
 * - Supports solid color, background-h, background-v.
 * - If a sprite layer exists (.bg-anim-sprite), seed that instead of section.style.
 * - Sets --section-bg and --section-bg-image for downstream consumers.
 */
export function applySectionBackground(
  section: HTMLElement,
  attrs: Record<string, any>,
  baseUrl?: string
): BgApplied {
  const sprite =
    section.querySelector<HTMLElement>(".bg-anim-sprite") ??
    section.querySelector<HTMLElement>(".bg-image-layer .bg-anim-sprite");

  // Decide orientation once
  const screenIsVertical = (() => {
    try { return window.matchMedia?.("(orientation: portrait)")?.matches ?? false; }
    catch { return false; }
  })();

  // Per-orientation position overrides (optional) with global fallback
  const posTokenGlobal = String(attrs["background-position"] ?? "").trim();
  const posTokenH = String(attrs["background-position-h"] ?? "").trim();
  const posTokenV = String(attrs["background-position-v"] ?? "").trim();

  const posH = normalizePos(posTokenH || posTokenGlobal || "center center");
  const posV = normalizePos(posTokenV || posTokenGlobal || "center center");

  // reset image var each pass
  section.style.removeProperty("--section-bg-image");

  // 1) Solid color
  if (!isOff(attrs["background-color"]) && isColor(String(attrs["background-color"]))) {
    const color = normalizeColor(String(attrs["background-color"]), "black");
    // clear any previous image
    if (sprite) {
      sprite.style.backgroundImage = "";
    } else {
      section.style.backgroundImage = "";
    }
    section.style.backgroundColor = color;
    section.style.setProperty("--section-bg", color);
    section.dataset.bgSeeded = "1";
    return "color";
  }

  // Deprecated warning (kept)
  if (attrs["background"] && !isOff(attrs["background"])) {
    console.warn("`background:` is deprecated. Use `background-color:` instead.");
  }

  // helper to resolve URL safely (empty string when none)
  const resolveUrl = (srcRaw: unknown): string => {
    if (isOff(srcRaw)) return "";
    const src = String(srcRaw).trim();
    if (!src) return "";
    // resolveImageSrc can return null → coalesce to empty string
    const abs = baseUrl ? (resolveImageSrc(baseUrl, src) ?? "") : src;
    if (!abs) return "";
    try {
      // minimal sanitization; encode spaces etc.
      return encodeURI(abs);
    } catch {
      return abs;
    }
  };

  // 2) Horizontal image (landscape)
  const hUrl = resolveUrl(attrs["background-h"]);
  // 3) Vertical image (portrait)
  const vUrl = resolveUrl(attrs["background-v"]);

  // If both provided, choose by current orientation
  if (hUrl && vUrl) {
    const target = sprite ?? section;
    if (screenIsVertical) {
      setBgImageTarget(target, vUrl, "contain", posV);
      log("[BG APPLIED]", {
        branch: "both-urls:portrait",
        target: target.className || target.tagName,
        url: (target.style.backgroundImage || "").slice(0, 160),
        size: target.style.backgroundSize,
        pos: target.style.backgroundPosition,
        orientation: "portrait",
        posH,
        posV
      });
      section.style.setProperty("--section-bg-image", `url("${vUrl}")`);
      section.style.backgroundColor = "";
      section.dataset.bgSeeded = "1";
      return "image-v";
    } else {
      setBgImageTarget(target, hUrl, "cover", posH);
      log("[BG APPLIED]", {
        branch: "both-urls:landscape",
        target: target.className || target.tagName,
        url: (target.style.backgroundImage || "").slice(0, 160),
        size: target.style.backgroundSize,
        pos: target.style.backgroundPosition,
        orientation: "landscape",
        posH,
        posV
      });
      section.style.setProperty("--section-bg-image", `url("${hUrl}")`);
      section.style.backgroundColor = "";
      section.dataset.bgSeeded = "1";
      return "image-h";
    }
  }

  // Only H provided → reuse for portrait with V position
  if (hUrl && !vUrl) {
    const target = sprite ?? section;
    if (screenIsVertical) {
      // Use the same asset in portrait; default to contain to avoid cropping
      setBgImageTarget(target, hUrl, "contain", posV);
      log("[BG APPLIED]", {
        branch: "h-only:portrait",
        target: target.className || target.tagName,
        url: (target.style.backgroundImage || "").slice(0, 160),
        size: target.style.backgroundSize,
        pos: target.style.backgroundPosition,
        orientation: "portrait",
        posH,
        posV
      });
      section.style.setProperty("--section-bg-image", `url("${hUrl}")`);
      section.style.backgroundColor = "";
      section.dataset.bgSeeded = "1";
      return "image-v";
    } else {
      setBgImageTarget(target, hUrl, "cover", posH);
      log("[BG APPLIED]", {
        branch: "h-only:landscape",
        target: target.className || target.tagName,
        url: (target.style.backgroundImage || "").slice(0, 160),
        size: target.style.backgroundSize,
        pos: target.style.backgroundPosition,
        orientation: "landscape",
        posH,
        posV
      });
      section.style.setProperty("--section-bg-image", `url("${hUrl}")`);
      section.style.backgroundColor = "";
      section.dataset.bgSeeded = "1";
      return "image-h";
    }
  }

  // Only V provided → reuse for landscape with H position
  if (!hUrl && vUrl) {
    const target = sprite ?? section;
    if (screenIsVertical) {
      setBgImageTarget(target, vUrl, "contain", posV);
      log("[BG APPLIED]", {
        branch: "v-only:portrait",
        target: target.className || target.tagName,
        url: (target.style.backgroundImage || "").slice(0, 160),
        size: target.style.backgroundSize,
        pos: target.style.backgroundPosition,
        orientation: "portrait",
        posH,
        posV
      });
      section.style.setProperty("--section-bg-image", `url("${vUrl}")`);
      section.style.backgroundColor = "";
      section.dataset.bgSeeded = "1";
      return "image-v";
    } else {
      // Use the same asset in landscape; default to cover for edge-to-edge
      setBgImageTarget(target, vUrl, "cover", posH);
      log("[BG APPLIED]", {
        branch: "v-only:landscape",
        target: target.className || target.tagName,
        url: (target.style.backgroundImage || "").slice(0, 160),
        size: target.style.backgroundSize,
        pos: target.style.backgroundPosition,
        orientation: "landscape",
        posH,
        posV
      });
      section.style.setProperty("--section-bg-image", `url("${vUrl}")`);
      section.style.backgroundColor = "";
      section.dataset.bgSeeded = "1";
      return "image-h";
    }
  }

  // nothing applied – clean up any stale direct background if no sprite
  if (!sprite) {
    section.style.backgroundImage = "";
  }
  // keep existing backgroundColor unless the author cleared it

  return "none";
}